import type { Transaction } from "@/context/FinanceContext";
import { PersistedState } from "./useFinanceState";
import { toast } from "@/hooks/use-toast";
import { markPending, releasePending, clearPending } from "./pendingWrites";

export function useTransactionsOperations(
  state: PersistedState,
  setState: React.Dispatch<React.SetStateAction<PersistedState>>,
  user: any,
  createTransaction: (data: any) => Promise<string>,
  updateSupabaseTransaction: (id: string, data: any) => Promise<boolean>,
  deleteTransaction: (id: string) => Promise<boolean>,
  syncFromSupabase: () => Promise<void>,
  bulkCreateTransactions?: (items: any[], onProgress?: (d: number, t: number) => void) => Promise<{ ids: string[]; inserted: number; failed: number; errors: string[] }>,
  bulkDeleteTransactions?: (ids: string[], onProgress?: (d: number, t: number) => void) => Promise<{ deleted: number; failed: number; errors: string[] }>,
) {
  const addTransaction = async (t: Omit<Transaction, "id">) => {
    // Sync to Supabase first if user is logged in
    if (user) {
      try {
        const supabaseTransactionId = await createTransaction({
          description: t.description,
          value: t.value,
          type: t.type,
          chart_of_accounts_id: t.categoryId || undefined,
          account_id: t.accountId || undefined,
          cost_center_id: t.costCenterId || undefined,
          payment_method_id: t.paymentMethodId || undefined,
          supplier_id: t.supplierId || undefined,
          issue_date: t.issueDate || undefined,
          due_date: t.dueDate || undefined,
          payment_date: t.paymentDate || undefined,
          settled: t.settled || false,
          parcel_mode: t.parcelMode || undefined,
          parcels: t.parcels || undefined,
          parcel_index: t.parcelIndex || undefined,
          issue_date_mode: t.issueDateMode || undefined,
          kind: t.kind || 'normal',
          transfer_group_id: t.transferGroupId || undefined,
          sale_id: t.saleId || undefined,
          purchase_id: t.purchaseId || undefined,
          reconciled: t.reconciled ?? false,
          reconciled_at: t.reconciledAt ?? undefined,
          ofx_fitid: t.ofxFitid ?? undefined,
          ofx_import_id: t.ofxImportId ?? undefined,
        });
        
        console.log('✅ Transação criada no Supabase com ID:', supabaseTransactionId);
        
        // Atualização imediata do estado local para responsividade
        const newTransaction = { ...t, id: supabaseTransactionId };
        setState((s) => ({
          ...s,
          transactions: [newTransaction, ...s.transactions.filter(tx => tx.id !== newTransaction.id)],
        }));
        
        // Success feedback
        toast({
          title: "✓ Lançamento criado",
          description: "Lançamento salvo com sucesso no Supabase.",
          duration: 2000,
        });
        
      } catch (error) {
        console.error('❌ Erro ao salvar transação no Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro ao criar lançamento",
          description: error instanceof Error ? error.message : "Não foi possível salvar o lançamento. Tente novamente.",
        });
        throw error; // Re-throw to prevent form closing
      }
    } else {
      // Fallback to local storage when offline
      const newTransaction = { ...t, id: `t-${crypto.randomUUID()}` };
      setState((s) => ({
        ...s,
        transactions: [newTransaction, ...s.transactions],
      }));
      
      toast({
        title: "Lançamento salvo localmente",
        description: "Faça login para sincronizar com a nuvem.",
      });
    }
  };

  // Converte um patch local em payload do Supabase preservando valores
  // explicitamente vazios (null/""), essenciais para estorno e limpeza de campos.
  const buildPayload = (patch: Partial<Transaction>) => {
    const map: Array<[keyof Transaction, string]> = [
      ["description", "description"],
      ["value", "value"],
      ["type", "type"],
      ["categoryId", "chart_of_accounts_id"],
      ["accountId", "account_id"],
      ["costCenterId", "cost_center_id"],
      ["paymentMethodId", "payment_method_id"],
      ["supplierId", "supplier_id"],
      ["issueDate", "issue_date"],
      ["dueDate", "due_date"],
      ["paymentDate", "payment_date"],
      ["settled", "settled"],
      ["parcelMode", "parcel_mode"],
      ["parcels", "parcels"],
      ["parcelIndex", "parcel_index"],
      ["issueDateMode", "issue_date_mode"],
      ["kind", "kind"],
      ["transferGroupId", "transfer_group_id"],
      ["saleId", "sale_id"],
      ["purchaseId", "purchase_id"],
      ["reconciled", "reconciled"],
      ["reconciledAt", "reconciled_at"],
      ["ofxFitid", "ofx_fitid"],
      ["ofxImportId", "ofx_import_id"],
    ];

    const payload: Record<string, any> = {};
    for (const [localKey, dbKey] of map) {
      if (Object.prototype.hasOwnProperty.call(patch, localKey)) {
        const v = (patch as any)[localKey];
        if (v !== undefined) payload[dbKey] = v;
      }
    }
    return payload;
  };

  const updateTransaction = async (id: string, patch: Partial<Transaction>) => {
    // Snapshot do estado ATUAL (não de uma cópia antiga) para revert confiável
    let previous: Transaction | undefined;

    setState((s) => {
      previous = s.transactions.find((t) => t.id === id);
      return {
        ...s,
        transactions: s.transactions.map((it) =>
          it.id === id ? { ...it, ...patch } : it
        ),
      };
    });

    if (!user) return;

    markPending(id);
    try {
      await updateSupabaseTransaction(id, buildPayload(patch));
      releasePending(id);
    } catch (error) {
      clearPending(id);
      // Reverte apenas o lançamento afetado, usando a lista atual
      if (previous) {
        const prev = previous;
        setState((s) => ({
          ...s,
          transactions: s.transactions.map((it) => (it.id === id ? prev : it)),
        }));
      }

      toast({
        variant: "destructive",
        title: "Erro ao atualizar lançamento",
        description:
          error instanceof Error
            ? error.message
            : "Não foi possível salvar as alterações. Tente novamente.",
      });
      throw error;
    }
  };

  const removeTransaction = async (id: string) => {
    let previous: Transaction | undefined;
    let previousIndex = -1;

    setState((s) => {
      previousIndex = s.transactions.findIndex((t) => t.id === id);
      previous = previousIndex >= 0 ? s.transactions[previousIndex] : undefined;
      return {
        ...s,
        transactions: s.transactions.filter((it) => it.id !== id),
      };
    });

    if (!user) {
      toast({
        title: "✓ Lançamento excluído",
        description: "Lançamento removido com sucesso.",
        duration: 1000,
      });
      return;
    }

    markPending(id);
    try {
      await deleteTransaction(id);
      releasePending(id);
      toast({
        title: "✓ Lançamento excluído",
        description: "Lançamento removido com sucesso.",
        duration: 1000,
      });
    } catch (error) {
      clearPending(id);
      // Restaura a linha na posição original
      if (previous) {
        const prev = previous;
        const idx = previousIndex;
        setState((s) => {
          if (s.transactions.some((t) => t.id === prev.id)) return s;
          const next = [...s.transactions];
          next.splice(idx >= 0 ? Math.min(idx, next.length) : next.length, 0, prev);
          return { ...s, transactions: next };
        });
      }

      toast({
        variant: "destructive",
        title: "Erro ao excluir lançamento",
        description:
          error instanceof Error
            ? error.message
            : "Não foi possível excluir o lançamento. Tente novamente.",
      });
      throw error;
    }
  };

  const toggleTransactionSettled = async (id: string) => {
    const transaction = state.transactions.find((t) => t.id === id);
    if (!transaction) return;

    const newSettledStatus = !transaction.settled;
    const newPaymentDate = newSettledStatus
      ? new Date().toISOString().split("T")[0]
      : null;

    // Uma única operação otimista + gravação (com revert próprio em caso de erro)
    await updateTransaction(id, {
      settled: newSettledStatus,
      paymentDate: newPaymentDate as any,
    });
  };

  const bulkUpdateTransactions = async (ids: string[], updates: Partial<Transaction>) => {
    const idSet = new Set(ids);

    // Snapshot do estado ATUAL para revert confiável
    let previous: Transaction[] = [];
    setState((s) => {
      previous = s.transactions.filter((t) => idSet.has(t.id));
      return {
        ...s,
        transactions: s.transactions.map((t) =>
          idSet.has(t.id) ? { ...t, ...updates } : t
        ),
      };
    });

    if (!user) return;

    const payload = buildPayload(updates);
    if (Object.keys(payload).length === 0) return;

    markPending(ids);
    try {
      const CHUNK = 20;
      for (let i = 0; i < ids.length; i += CHUNK) {
        await Promise.all(
          ids.slice(i, i + CHUNK).map((id) => updateSupabaseTransaction(id, payload))
        );
      }
      releasePending(ids);
    } catch (error) {
      clearPending(ids);
      const prevMap = new Map(previous.map((t) => [t.id, t]));
      setState((s) => ({
        ...s,
        transactions: s.transactions.map((t) => prevMap.get(t.id) || t),
      }));

      toast({
        variant: "destructive",
        title: "Erro ao atualizar lançamentos",
        description:
          error instanceof Error
            ? error.message
            : "Não foi possível salvar as alterações. Tente novamente.",
        duration: 5000,
      });

      throw error;
    }
  };

  // ===== BULK ADD (importação) =====
  const bulkAddTransactions = async (
    items: Array<Omit<Transaction, "id">>,
    onProgress?: (done: number, total: number) => void
  ): Promise<{ inserted: number; failed: number; errors: string[] }> => {
    if (!user || !bulkCreateTransactions) {
      // Fallback offline: salvar localmente
      const newItems = items.map((t) => ({ ...t, id: `t-${crypto.randomUUID()}` }));
      setState((s) => ({ ...s, transactions: [...newItems, ...s.transactions] }));
      return { inserted: items.length, failed: 0, errors: [] };
    }

    const payload = items.map((t) => ({
      description: t.description,
      value: t.value,
      type: t.type,
      chart_of_accounts_id: t.categoryId || undefined,
      account_id: t.accountId || undefined,
      cost_center_id: t.costCenterId || undefined,
      payment_method_id: t.paymentMethodId || undefined,
      supplier_id: t.supplierId || undefined,
      issue_date: t.issueDate || undefined,
      due_date: t.dueDate || undefined,
      payment_date: t.paymentDate || undefined,
      settled: t.settled ?? false,
      parcel_mode: t.parcelMode || undefined,
      parcels: t.parcels ?? undefined,
      parcel_index: t.parcelIndex ?? undefined,
      issue_date_mode: t.issueDateMode || undefined,
      kind: t.kind || 'normal',
      transfer_group_id: t.transferGroupId || undefined,
      sale_id: t.saleId || undefined,
      purchase_id: t.purchaseId || undefined,
    }));

    const result = await bulkCreateTransactions(payload, onProgress);
    // Sincronizar estado completo para refletir dados reais persistidos
    try { await syncFromSupabase(); } catch {}
    return { inserted: result.inserted, failed: result.failed, errors: result.errors };
  };

  // ===== BULK REMOVE (exclusão em massa) =====
  const bulkRemoveTransactions = async (
    ids: string[],
    onProgress?: (done: number, total: number) => void
  ): Promise<{ deleted: number; failed: number; errors: string[] }> => {
    if (ids.length === 0) return { deleted: 0, failed: 0, errors: [] };

    // Snapshot para revert em caso de falha
    const snapshot = state.transactions.filter((t) => ids.includes(t.id));

    // UI otimista
    setState((s) => ({
      ...s,
      transactions: s.transactions.filter((t) => !ids.includes(t.id)),
    }));

    if (!user || !bulkDeleteTransactions) {
      return { deleted: ids.length, failed: 0, errors: [] };
    }

    markPending(ids);
    let result: { deleted: number; failed: number; errors: string[] };
    try {
      result = await bulkDeleteTransactions(ids, onProgress);
    } finally {
      clearPending(ids);
    }

    if (result.failed > 0) {
      // Reverte exclusão otimista para restaurar consistência
      setState((s) => ({
        ...s,
        transactions: [...snapshot, ...s.transactions],
      }));
    }
    // Re-sync para garantir consistência absoluta com o banco
    try { await syncFromSupabase(); } catch {}

    return result;
  };

  return {
    addTransaction,
    updateTransaction,
    removeTransaction,
    toggleTransactionSettled,
    bulkUpdateTransactions,
    bulkAddTransactions,
    bulkRemoveTransactions,
  };
}
